import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/lib/hooks/useToast'
import { useCreatePracticeCase } from '@/api/practice-cases'
import { Upload, X, FileText } from 'lucide-react'
import { useHasPermission } from '@/lib/hooks/usePermission'

interface CreatePracticeCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const ALLOWED_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
]

const ALLOWED_EXTENSIONS = ['docx', 'txt', 'md']
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const MAX_FILES = 1

export function CreatePracticeCaseDialog({ open, onOpenChange }: CreatePracticeCaseDialogProps) {
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [location, setLocation] = useState('')
  const [numArticles, setNumArticles] = useState(15)
  const [files, setFiles] = useState<File[]>([])
  const [errors, setErrors] = useState<string[]>([])

  const { mutateAsync: createPracticeCase, isPending } = useCreatePracticeCase()
  const canCreatePractice = useHasPermission('practice.create')

  const validateFile = (file: File): string | null => {
    // Check file type
    const extension = file.name.split('.').pop()?.toLowerCase()
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return `Bestandstype niet toegestaan: ${file.name} (toegestaan: ${ALLOWED_EXTENSIONS.join(', ')})`
    }

    if (!ALLOWED_TYPES.includes(file.type) && file.type !== '') {
      return `MIME-type niet toegestaan: ${file.name}`
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return `Bestand te groot: ${file.name} (max 10MB)`
    }

    return null
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    const newErrors: string[] = []

    // Check total count
    if (files.length + selectedFiles.length > MAX_FILES) {
      newErrors.push(`Maximum ${MAX_FILES} bestand toegestaan`)
      setErrors(newErrors)
      return
    }

    // Validate each file
    const validFiles: File[] = []
    for (const file of selectedFiles) {
      const error = validateFile(file)
      if (error) {
        newErrors.push(error)
      } else {
        validFiles.push(file)
      }
    }

    if (newErrors.length > 0) {
      setErrors(newErrors)
    } else {
      setErrors([])
    }

    setFiles([...files, ...validFiles])
    // Reset input
    e.target.value = ''
  }

  const handleRemoveFile = (index: number) => {
    setFiles(files.filter((_, i) => i !== index))
    setErrors([])
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors([])

    // Validation
    if (!title?.trim()) {
      setErrors(['Titel is verplicht'])
      return
    }

    if (!location?.trim()) {
      setErrors(['Locatie is verplicht'])
      return
    }

    if (files.length === 0) {
      setErrors(['Bestand is verplicht'])
      return
    }

    try {
      await createPracticeCase({
        title: title.trim(),
        location: location.trim(),
        numArticles,
        files,
      })

      toast({
        title: 'Oefenscenario aangemaakt',
        description: `Oefenscenario "${title}" wordt nu gegenereerd. Dit kan enkele minuten duren.`,
        variant: 'success',
      })

      // Reset form
      setTitle('')
      setLocation('')
      setNumArticles(15)
      setFiles([])
      setErrors([])
      onOpenChange(false)
    } catch (error) {
      console.error('Failed to create practice case:', error)
      toast({
        title: 'Fout',
        description: 'Kon oefenscenario niet aanmaken. Probeer het opnieuw.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nieuw oefenscenario</DialogTitle>
          <DialogDescription>
            Maak een nieuw oefenscenario voor trainingsoefeningen. Upload een voorbeeld
            omgevingsbeeld analyse of gedetailleerde beschrijving van de oefensituatie.
          </DialogDescription>
        </DialogHeader>

        {!canCreatePractice && (
          <div className="mb-4 p-3 text-sm text-orange-600 bg-orange-50 border border-orange-200 rounded-md dark:bg-orange-950/20 dark:text-orange-400 dark:border-orange-800">
            U heeft geen toestemming om oefenscenario's aan te maken.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-3">
              <ul className="list-disc list-inside text-sm text-red-600 dark:text-red-400">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="title">
              Titel <span className="text-red-500">*</span>
            </Label>
            <Input
              id="title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Bijv. Brand in winkelcentrum"
              required
              aria-required="true"
              disabled={!canCreatePractice}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">
              Locatie <span className="text-red-500">*</span>
            </Label>
            <Input
              id="location"
              value={location}
              onChange={e => setLocation(e.target.value)}
              placeholder="Bijv. Den Haag, Winkelcentrum De Passage"
              required
              aria-required="true"
              disabled={!canCreatePractice}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="numArticles">Aantal artikelen</Label>
            <Input
              id="numArticles"
              type="number"
              min={5}
              max={30}
              value={numArticles}
              onChange={e => setNumArticles(Number(e.target.value))}
              placeholder="15"
              aria-describedby="numArticles-description"
              disabled={!canCreatePractice}
            />
            <p id="numArticles-description" className="text-sm text-muted-foreground">
              Kies hoeveel artikelen er gegenereerd moeten worden (5-30, standaard: 15)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="files">
              Bestanden <span className="text-red-500">*</span>
            </Label>
            <p className="text-sm text-muted-foreground">
              Upload 1 bestand (max 10MB). Dit moet een voorbeeld omgevingsbeeld analyse of
              gedetailleerde beschrijving van de oefensituatie zijn. Toegestane types: DOCX, TXT, MD
            </p>

            {files.length < MAX_FILES && (
              <div className="flex items-center gap-2">
                <Input
                  id="files"
                  type="file"
                  multiple
                  accept=".docx,.txt,.md"
                  onChange={handleFileChange}
                  className="cursor-pointer"
                  aria-label="Upload bestanden"
                  disabled={!canCreatePractice}
                />
                <Upload className="h-5 w-5 text-muted-foreground" />
              </div>
            )}

            {files.length > 0 && (
              <div className="space-y-2 mt-3">
                {files.map((file, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted rounded-md"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm truncate">{file.name}</span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        ({(file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(index)}
                      aria-label={`Verwijder ${file.name}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Annuleren
            </Button>
            <Button type="submit" disabled={isPending || !canCreatePractice} variant="gradient">
              {isPending ? 'Aanmaken...' : 'Oefenscenario aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
